import { GET_LIST, GET_ONE, GET_MANY, GET_MANY_REFERENCE, CREATE, UPDATE, DELETE } from "react-admin";
import uuid from "react-uuid";
import * as Sentry from "@sentry/browser";
import {
  selectModifiedValues,
  stripAuthHeaders,
  decryptHereKey,
  rejectPromise,
  filterResources,
  getBillingAccountByUUID,
  handleEmpty,
} from "./DataProviderUtils";
import { sendSentryException, throwSentryError } from "./SentryErrorHandler";

const API_URL = process.env.REACT_APP_API_URL;
const PLATFORM_API_URL = API_URL + "/api/v1";
const LAMBDA_URL =
  process.env?.REACT_APP_TESTING === "true"
    ? "http://localhost:3001/BogusLambda"
    : process.env?.REACT_APP_LAMBDA_URL || "http://localhost:3001/BogusLambda";

// default format for outgoing requests
const baseOutgoingRequest = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST: {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = new URLSearchParams({
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      });
      return { url: `${API_URL}/${resourcePath}?${query.toString()}` };
    }
    case GET_ONE:
      return { url: `${API_URL}/${resourcePath}/${params.id}` };
    case GET_MANY: {
      const query = new URLSearchParams({
        filter: JSON.stringify({ id: params.ids }),
      });
      return { url: `${API_URL}/${resourcePath}?${query.toString()}` };
    }
    case GET_MANY_REFERENCE: {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = new URLSearchParams({
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify({ ...params.filter, [params.target]: params.id }),
      });
      return { url: `${API_URL}/${resourcePath}?${query.toString()}` };
    }
    case UPDATE:
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
        options: { method: "PATCH", body: JSON.stringify(params.data) },
      };
    case CREATE:
      return {
        url: `${API_URL}/${resourcePath}`,
        options: { method: "POST", body: JSON.stringify(params.data) },
      };
    case DELETE:
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
        options: { method: "DELETE" },
      };
    default:
      return Promise.reject("data provider request failed");
  }
};

// base outgoing platform api requests (forms, screens, etc.)
const outgoingClientResource = (type, resourcePath, params) => {
  var options = {};
  options.headers = new Headers();
  options.headers.set("eleos-admin-client-key", params.id);
  switch (type) {
    case GET_LIST: {
      return {
        url: `${PLATFORM_API_URL}/${resourcePath}`,
        options,
      };
    }
    case GET_ONE: {
      return {
        url: ["assets"].includes(resourcePath)
          ? `${PLATFORM_API_URL}/${resourcePath}/${params.handle}`
          : `${PLATFORM_API_URL}/${resourcePath}`,
        options,
      };
    }
    case GET_MANY: {
      return {
        url: `${PLATFORM_API_URL}/${resourcePath}`,
        options,
      };
    }
    case GET_MANY_REFERENCE: {
      return {
        url: `${PLATFORM_API_URL}/${resourcePath}`,
        options,
      };
    }
    case UPDATE:
      options = {
        method: "PUT",
        headers: {
          "eleos-admin-client-key": params.id,
        },
        body: !["dashboards", "menus"].includes(resourcePath) ? JSON.stringify(params.data) : params.data,
      };
      return { url: `${PLATFORM_API_URL}/${resourcePath}/${params.code}`, options };
    case CREATE:
      options = {
        method: "POST",
        headers: {
          "eleos-admin-client-key": params.id,
        },
        body: JSON.stringify(params.data),
      };
      return { url: `${PLATFORM_API_URL}/${resourcePath}`, options };
    default:
      return Promise.reject("data provider request failed");
  }
};

const outgoingBackendUser = (type, resourcePath, params) => {
  var options = {};
  options.headers = new Headers();
  if (type === "GET_MANY_REFERENCE") {
    if (params.target === "key") {
      return {
        url: `${API_URL}/internal/clients/${params.id}/${resourcePath}`,
        options,
      };
    } else if (params.target === "uuid") {
      return {
        url: `${API_URL}/internal/customers/${params.id}/${resourcePath}`,
        options,
      };
    } else {
      return Promise.reject("invalid target");
    }
  } else if (type === "GET_ONE") {
    return {
      url: `${API_URL}/internal/backend_users/${params.id}`,
      options,
    };
  } else if (type === "UPDATE") {
    if (params?.resendInvite) {
      return {
        url: `${API_URL}/internal/backend_users/resend_invite/${params.email}`,
        options: { method: "PUT" },
      };
    }
    if (params.data.active != null) {
      return {
        url: `${API_URL}/internal/backend_users/${params.id}`,
        options: { method: "PATCH", body: JSON.stringify(params.data) },
      };
    } else if (params.previousData.record !== undefined) {
      return {
        url: `${API_URL}/internal/backend_users/${params.previousData.record.id}/roles`,
        options: { method: "PATCH", body: JSON.stringify(params.data) },
      };
    } else {
      return {
        url: `${API_URL}/internal/backend_users/${params.previousData.id}`,
        options: {
          method: "PATCH",
          body: JSON.stringify({
            allow_ftp_downloads: params.data.allow_ftp_downloads,
          }),
        },
      };
    }
  } else {
    return Promise.reject("data provider request failed");
  }
};

const incomingBackendUsers = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      json.map((x) => {
        Object.assign(x, { id: x.email });
      });
      if (params.filter != null && params.filter.roles != null && params.filter.roles.length > 0) {
        json = json.filter((d) => d.roles.some((e) => e.code === params.filter.roles));
      }
      if (params.filter != null && params.filter.active != null && params.filter.active.length > 0) {
        json = json.filter((d) => d.active === JSON.parse(params.filter.active));
      }
      return {
        data: json,
        total: json.length,
      };
    case UPDATE:
      const id = params?.resendInvite ? params.email : json.email;
      return {
        data: { id: id },
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.email }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

// default format for incoming request data
const baseIncomingRequest = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      return {
        data: json,
        total: json.length,
      };
    case GET_MANY_REFERENCE:
      const data = json.length === undefined ? [json] : json;
      return {
        data,
        total: json.length,
      };
    case GET_ONE:
      if ("id" in json) {
        return {
          data: json,
        };
      }
      return { data: Object.assign(json, { id: uuid() }) };
    case CREATE:
      return {
        data: { ...params.data },
      };
    case UPDATE:
      return {
        data: { ...params.data },
      };
    default:
      return { data: json };
  }
};

const outgoingClients = (type, params) => {
  switch (type) {
    case UPDATE: {
      var payload = selectModifiedValues(params.previousData, params.data);
      payload = payload == null ? {} : payload;
      if (payload.apns_app === null) {
        payload.apns_app = {
          private_key: null,
          certificate: null,
        };
      }
      return {
        url: `${API_URL}/clients/${params.id}`,
        options: { method: "PATCH", body: JSON.stringify(payload) },
      };
    }
    case GET_MANY_REFERENCE:
      return baseOutgoingRequest(type, "clients", params);
    case CREATE: {
      if (params.data.role === "sandbox" && !params.data.key.endsWith("-dev")) {
        alert("A client of type 'sandbox' must have a client key ending with '-dev'");
        return {};
      } else if (params.data.role === "guest") {
        if (!params.data.key.endsWith("-guest") || !params.data.name.endsWith("(guest)")) {
          alert(
            "A client of type 'guest' must have a client key ending with '-guest' and a name ending with '(guest)'"
          );
          return {};
        } else if (!params.data.client_group_key) {
          alert("A guest client must be within an existing client group.");
          return {};
        }
      }
      return {
        url: `${API_URL}/internal/clients`,
        options: { method: "POST", body: JSON.stringify(params.data) },
      };
    }
    default: {
      return baseOutgoingRequest(type, "clients", params);
    }
  }
};

const parseClientResourceAttribute = (json, resource_attribute, resource) => {
  json.forEach(function (object) {
    if (object.hasOwnProperty(resource_attribute) && typeof object[resource_attribute] === "string") {
      try {
        object[resource_attribute] = JSON.parse(object[resource_attribute]);
      } catch (e) {
        const sentryError = new Error(
          `Could not parse the ${resource} ${resource_attribute} string: ${object[resource_attribute]}`
        );
        sentryError.name = `Error Parsing Client Resource ${resource_attribute}`;
        Sentry.captureException(sentryError, {
          tags: { resource },
        });
      }
    }
  });
};

const parseClientErrors = (json) => {
  json.forEach(function (object) {
    switch (object.type) {
      // grab a readable error messages for each error type
      // schema error will be left as is
      case "json":
        object["error_details"] = object.json_error.message;
        break;
      case "http":
        object["error_details"] =
          `Expected http status: ${object.http_error.expected_status}. Actual http status: ${object.http_error.actual_status}`;
        object["http_status"] = object.http_error.actual_status;
        break;
      case "network":
        object["error_details"] = object.network_error.message;
        break;
      case "other":
        object["error_details"] = object.other_error.message;
        break;
      default:
        object["error_details"] = "";
    }
    stripAuthHeaders(object);
    // sets the id for the object, requried by react iterators
    object["id"] = object.uuid;
    // pulls out the service name from the service object if it exists
    if (object.service && object.service.name) {
      object["service_name"] = object.service.name;
    }
  });
};

const incomingClientResource = (json, type, resource, params) => {
  if (type === "UPDATE") {
    return { data: { id: params?.id } };
  }
  if (type !== "GET_ONE") {
    json.map((x) => {
      if (x.id == null) {
        if (resource === "backend_users") {
          Object.assign(x, { id: x.email });
        } else {
          Object.assign(x, { id: uuid() });
        }
      }
    });
    switch (resource) {
      case "errors":
        parseClientErrors(json);
        break;
      case "menus":
      case "dashboards":
        parseClientResourceAttribute(json, "items", resource);
        break;
      case "backend_users":
        if (type == "UPDATE") {
          return {
            data: Object.assign(json, { id: uuid() }),
          };
        } else {
          parseClientResourceAttribute(json, "roles", resource);
        }
        break;
      default:
        parseClientResourceAttribute(json, "definition", resource);
    }
  }
  return baseIncomingRequest(json, type, params);
};

const incomingClients = (json, type, params) => {
  switch (type) {
    case CREATE:
      var newJson = Object.assign(json, { id: json.key });
      return { data: newJson };
    case GET_LIST:
      if (params.filter && "q" in params.filter) {
        const query = params.filter.q.toLowerCase();
        json = json.filter(
          (item) => item.key.toLowerCase().includes(query) || (item.name && item.name.toLowerCase().includes(query))
        );
      } else {
        json = filterResources(json, params.filter);
      }
      return {
        data: json.map((x) => Object.assign(x, { id: x.key })),
        total: json.length,
      };
    case GET_MANY_REFERENCE:
      if (params.target === "billing_account_id") {
        return {
          data: json.filter((x) => x.billing_account_id === params.id).map((x) => Object.assign(x, { id: x.key })),
          total: json.length,
        };
      } else {
        return {
          total: json.length,
          data: json.map((x) => Object.assign(x, { id: x.key })),
        };
      }
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.key }),
      };
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.key })),
        total: json.length,
      };
    case UPDATE:
      return { data: { id: json.key } };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingAppleAccounts = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.apple_team_identifier })),
        total: json.length,
      };
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.apple_team_identifier })),
        total: json.length,
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.apple_team_identifier }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingClientGroups = (json, type, params) => {
  switch (type) {
    case CREATE:
      var newJson = Object.assign(json, { id: json.key });
      return { data: newJson };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json["key"] }),
      };
    case GET_LIST:
      // this is to handle AutocompleteInputs, we actually don't want to do any manually filter if the filter is from an automcomplete input
      // we check if this request is from an autocomlete input by seeing if the 'q' key is in the filter object, otherwise filter like normal
      if (params.filter && "q" in params.filter) {
        const query = params.filter.q.toLowerCase();
        json = json.filter(
          (item) => item.key.toLowerCase().includes(query) || (item.name && item.name.toLowerCase().includes(query))
        );
      } else {
        json = filterResources(json, params.filter);
      }
      return {
        data: json.map((x) => Object.assign(x, { id: x.key })),
        total: json.length,
      };
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.key })),
        total: json.length,
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const getBillingAccount = (json, params) => {
  const account = json.filter((x) => x.external_id === params.id);
  if (account.length < 1 || account.length > 1) return {};
  else return account[0];
};

const incomingBillingAccounts = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      json = [getBillingAccountByUUID(json, params.id)];
      return {
        data: json.map((x) => Object.assign(x, { id: x.name })),
        total: json.length,
      };
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.external_id })),
        total: json.length,
      };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.external_id })),
        total: json.length,
      };
    case GET_ONE:
      const billingAccount = getBillingAccount(json, params);
      return {
        data: Object.assign(billingAccount, { id: billingAccount.external_id }),
      };
    case CREATE:
      return {
        data: Object.assign(json, { id: json.name }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingBillingAccounts = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY:
      return { url: `${API_URL}/${resourcePath}` };
    case GET_LIST:
    case GET_MANY_REFERENCE: {
      const emptyFilter = Object.keys(params?.filter).length === 0;
      if (!emptyFilter) {
        // Remap `q` to `name` if present. This ensures the AutoCompleteInput's work.
        if (params.filter && "q" in params.filter) {
          params.filter.name = params.filter.q;
          delete params.filter.q;
        }
        // Build the query string
        const query = new URLSearchParams(
          Object.entries(params.filter).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              acc[key] = value;
            }
            return acc;
          }, {})
        );
        return {
          url: `${API_URL}/${resourcePath}?${query.toString()}`,
        };
      }
      return { url: `${API_URL}/${resourcePath}` };
    }
    case GET_ONE:
      return { url: `${API_URL}/${resourcePath}` };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBillingContractStructures = (json, type, params) => {
  switch (type) {
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.code })),
        total: json.length,
      };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.code })),
        total: json.length,
      };
    case GET_ONE:
      const structures = json.filter((x) => x.code === params.id);
      const structure = structures.length < 1 || structures.length > 1 ? {} : structures[0];
      return {
        data: Object.assign(structure, { id: structure.code }),
      };
    case CREATE:
      return {
        data: Object.assign(json, { id: json.code }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingBillingContractStructures = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY:
      return { url: `${API_URL}/${resourcePath}` };
    case GET_LIST:
      const query = new URLSearchParams({
        code: params.filter.code,
      });
      return params?.filter?.code
        ? { url: `${API_URL}/${resourcePath}?${query.toString()}` }
        : { url: `${API_URL}/${resourcePath}` };
    case GET_ONE:
      return { url: `${API_URL}/${resourcePath}` };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBillingContractStructureProducts = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return {
        total: json.length,
        data: json.map((x) => Object.assign(x, { id: x.product_code })),
      };
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.contract_structure_code })),
        total: json.length,
      };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.contract_structure_code })),
        total: json.length,
      };
    case CREATE:
      return {
        data: Object.assign(json, { id: json.contract_structure_code }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingBillingContractStructureProducts = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return { url: `${API_URL}/${resourcePath}/${params.filter.id}` };
    case GET_LIST:
      return { url: `${API_URL}/${resourcePath}/${params.filter.id}` };
    case GET_MANY:
      return { url: `${API_URL}/${resourcePath}` };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingPerLoadBillingStructure = (json, type, params) => {
  switch (type) {
    case CREATE:
      return {
        data: Object.assign(json, { id: json.billing_account_external_id }),
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: json.billing_account_external_id }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingPerLoadBillingStructure = (type, resourcePath, params) => {
  switch (type) {
    case CREATE:
      return {
        url: `${API_URL}/${resourcePath}`,
        options: { method: "POST", body: JSON.stringify(params.data) },
      };
    case UPDATE:
      return {
        url: `${API_URL}/${resourcePath}`,
        options: { method: "PATCH", body: JSON.stringify(params.data) },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBillingAccountContracts = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return {
        total: json.length,
        data: json.map((x) => Object.assign(x, { id: x.external_id })),
      };
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.external_id })),
        total: json.length,
      };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.external_id })),
        total: json.length,
      };
    case GET_ONE:
      let acct_contract = json.filter((x) => x.external_id === params.id)(
        acct_contract.length < 1 || acct_contract.length > 1
      )
        ? (acct_contract = {})
        : (acct_contract = acct_contract[0]);
      return {
        data: Object.assign(acct_contract, { id: acct_contract.id }),
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: json.billing_account_external_id }),
        options: { method: "PATCH", body: JSON.stringify(params.data) },
      };
    case CREATE:
      return {
        data: Object.assign(json, { id: json.billing_account_external_id }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingBillingAccountContracts = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY:
      return { url: `${API_URL}/${resourcePath}` };
    case GET_MANY_REFERENCE:
      return { url: `${API_URL}/${resourcePath}/${params.id}` };
    case GET_LIST:
      return { url: `${API_URL}/${resourcePath}/${params.id}` };
    case UPDATE:
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
        options: { method: "PATCH", body: JSON.stringify(params.data) },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBillingProducts = (json, type, params) => {
  switch (type) {
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.code })),
        total: json.length,
      };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.code })),
        total: json.length,
      };
    case GET_ONE:
      return {
        data: { ...json[0], id: json.length > 0 ? json[0].code : "empty" },
      };
    case CREATE:
      return {
        data: Object.assign(json, { id: json.code }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingBillingProducts = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY:
      return { url: `${API_URL}/${resourcePath}` };
    case GET_LIST:
      const query = new URLSearchParams({
        code: params.filter.code,
      });
      return params?.filter?.code
        ? { url: `${API_URL}/${resourcePath}?${query.toString()}` }
        : { url: `${API_URL}/${resourcePath}` };
    case GET_ONE:
      return { url: `${API_URL}/${resourcePath}/${params.id}` };
    case CREATE:
      let body = { ...params.data };
      /*
       *  Squid only accepts raw JSON for application_filters.
       *  Because of this, we can not just JSON.stringify the body.
       *  Check and convert application_filters to raw JSON
       */
      if (body.application_filters) {
        body.application_filters = JSON.parse(body.application_filters);
      }
      return {
        url: `${API_URL}/${resourcePath}`,
        options: { method: "POST", body: JSON.stringify(body) },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBillingPriceSheets = (json, type, params) => {
  switch (type) {
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.code })),
        total: json.length,
      };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.code })),
        total: json.length,
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.code }),
      };
    case CREATE:
      return {
        data: Object.assign(json, { id: json.code }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingBillingPriceSheets = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY:
      return { url: `${API_URL}/${resourcePath}` };
    case GET_LIST:
      const query = new URLSearchParams({
        code: params.filter.code,
      });
      return params?.filter?.code
        ? { url: `${API_URL}/${resourcePath}?${query.toString()}` }
        : { url: `${API_URL}/${resourcePath}` };
    case CREATE:
      return {
        url: `${API_URL}/${resourcePath}`,
        options: { method: "POST", body: JSON.stringify(params.data) },
      };
    case GET_ONE:
      return { url: `${API_URL}/${resourcePath}/${params.id}` };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBillingPriceSheetItems = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return {
        total: json.length,
        data: json.map((x, index) => Object.assign(x, { id: index + 1 })),
      };
    case GET_MANY:
      return {
        total: json.length,
        data: json.map((x, index) => Object.assign(x, { id: index + 1 })),
      };
    case GET_LIST:
      return {
        total: json.length,
        data: json.map((x, index) => Object.assign(x, { id: index + 1 })),
      };
    case CREATE:
      return {
        data: Object.assign(json, { id: json.price_sheet_code }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingBillingPriceSheetItems = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY:
      return { url: `${API_URL}/${resourcePath}` };
    case GET_MANY_REFERENCE:
      return { url: `${API_URL}/${resourcePath}/${params.id}` };
    case GET_LIST:
      return { url: `${API_URL}/${resourcePath}/${params.id}` };
    case UPDATE:
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
        options: { method: "PATCH", body: JSON.stringify(params.data) },
      };
      if (params.filter?.id) {
        return { url: `${API_URL}/${resourcePath}/${params.filter.id}` };
      }
      return { url: `${API_URL}/${resourcePath}/${params.id}` };
    case CREATE:
      return {
        url: `${API_URL}/${resourcePath}`,
        options: { method: "POST", body: JSON.stringify(params.data) },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingCustomers = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      const basePath = `${API_URL}/${resourcePath}`;
      var urlString = basePath;
      if (params.filter.customer_name != null && params.filter.customer_name.length > 0) {
        urlString = basePath.concat(`?customer_name=${params.filter.customer_name}`);
      }
      if (params.filter.email != null && params.filter.email.length > 0) {
        if (urlString == basePath) {
          urlString = basePath.concat(`?email=${params.filter.email}`);
        } else {
          urlString = urlString.concat(`&email=${params.filter.email}`);
        }
      }
      if (urlString == basePath) params.filter = null;
      return { url: urlString };
    case GET_ONE:
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
      };
    case UPDATE:
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
        options: { method: "PATCH", body: JSON.stringify(params.data) },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingDocumentFormats = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY:
      return {
        url: `${API_URL}/internal/document_formats`,
      };
    case GET_ONE:
      return {
        url: `${API_URL}/internal/document_formats`,
      };
    case GET_LIST:
      return {
        url: `${API_URL}/internal/document_formats`,
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingCharge = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return {
        url: `${API_URL}/internal/customers/${params.id}/${resourcePath}`,
      };
    case GET_ONE:
      return {
        url: `${API_URL}/internal/customers/${params.id[0]}/${resourcePath}/${params.id[1]}`,
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingStripeCharges = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return {
        url: `${API_URL}/internal/customers/${params.target}/${resourcePath}`,
      };
    case GET_ONE:
      return {
        url: `${API_URL}/internal/customers/${resourcePath}/${params.id}`,
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingStripeSubscriptions = (type, resourcePath, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return {
        url: `${API_URL}/internal/customers/invoices/${params.target}/${resourcePath}`,
      };
    case GET_ONE:
      return {
        url: `${API_URL}/internal/customers/invoices/${params.id}/${resourcePath}`,
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingDriveAxleUsers = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      return {
        url: `${API_URL}/${resourcePath}/${params.filter.email}`,
      };
    case GET_ONE:
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
      };
    case UPDATE:
      return {
        url: `${API_URL}/${resourcePath}/send_password_reset/${params.id}`,
        options: { method: "PUT" },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingDocuments = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      const basePath = `${API_URL}/${resourcePath}/search?`;
      var urlString = basePath;
      const search_params = new URLSearchParams();

      if (params.filter.uuid != null && params.filter.uuid.length > 0) {
        const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
        if (regexExp.test(params.filter.uuid)) {
          search_params.append("customer_uuid", params.filter.uuid);
        } else {
          search_params.append("client_key", params.filter.uuid);
        }
      }

      if (params.filter.sender_email != null && params.filter.sender_email.length > 0) {
        search_params.append("driver_email", params.filter.sender_email);
      }
      if (params.filter.recipient_email != null && params.filter.recipient_email.length > 0) {
        search_params.append("recipient_email", params.filter.recipient_email);
      }
      if (search_params.size > 0) {
        if (params.filter.start_date != null && params.filter.start_date.length > 0) {
          search_params.append("start_date", params.filter.start_date);
        }
        if (
          params.filter.start_date != null &&
          params.filter.start_date.length > 0 &&
          params.filter.end_date != null &&
          params.filter.end_date.length > 0
        ) {
          search_params.append("end_date", params.filter.end_date);
        }
        if (params.filter.downloaded_documents == undefined) {
          search_params.append("is_downloaded", false);
        } else {
          search_params.append("is_downloaded", true);
        }
      }
      if (search_params.size == 0) {
        params.filter = null;
      } else {
        urlString += search_params;
      }
      return {
        url: urlString,
      };
    case UPDATE:
      return {
        url: `${API_URL}/internal/document/update/`,
        options: {
          method: "PATCH",
          body: params.body,
        },
      };
    case GET_ONE:
      return {
        url: `${API_URL}/internal/documents/${params.id}`,
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingApps = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
    case GET_MANY:
      if (params?.meta?.statuses) {
        let urlString = `${API_URL}/${resourcePath}/${params.id}/statuses`;
        if (params?.filter?.tag) {
          const query = new URLSearchParams({ tag: params.filter.tag });
          urlString += `?${query.toString()}`;
        }

        return { url: urlString };
      }
      var urlString = `${API_URL}/${resourcePath}`;
      return { url: urlString };
    case GET_ONE:
      if (params?.meta?.status) {
        return { url: `${API_URL}/${resourcePath}/status/${params.id}` };
      }
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
      };
    case UPDATE:
      if (params.meta) {
        return {
          url: `${API_URL}/${resourcePath}/${params.id}/published/${params.meta.checked}`,
          options: { method: "PUT" },
        };
      }
      var updated_values = selectModifiedValues(params.previousData, params.data);
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
        options: { method: "PATCH", body: JSON.stringify(updated_values) },
      };
    case CREATE:
      if (params?.switcher) {
        return {
          url: `${API_URL}/${resourcePath}/switcher/${params.app_client_key}/${params.destinationKey}`,
          options: { method: "POST" },
        };
      }
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingSendgrid = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      if (params?.meta?.isFreeLoaderSearch) {
        return { url: `${API_URL}/internal/sendgrid/status/${params.id}` };
      }
      if (params?.meta?.activity) {
        return { url: `${API_URL}/internal/sendgrid/messages/${params.id}` };
      }
      return { url: `${API_URL}/internal/backend_users/sendgrid_status/${params.id}` };
    case GET_LIST:
      return { url: `${API_URL}/internal/sendgrid/messages?${params.query_string}` };
    case DELETE:
      switch (params.meta) {
        case "Blocks":
          if (params?.isFreeLoaderSearch) {
            return {
              url: `${API_URL}/internal/sendgrid/blocks/${params.id}`,
              options: { method: "DELETE" },
            };
          }
          return {
            url: `${API_URL}/internal/backend_users/sendgrid/blocks/${params.id}`,
            options: { method: "DELETE" },
          };
        case "Bounce":
          if (params?.isFreeLoaderSearch) {
            return {
              url: `${API_URL}/internal/sendgrid/bounces/${params.id}`,
              options: { method: "DELETE" },
            };
          }
          return {
            url: `${API_URL}/internal/backend_users/sendgrid/bounces/${params.id}`,
            options: { method: "DELETE" },
          };
        case "Unsubscribe":
          if (params?.isFreeLoaderSearch) {
            return {
              url: `${API_URL}/internal/sendgrid/unsubscribes/global/${params.id}`,
              options: { method: "DELETE" },
            };
          }
          return {
            url: `${API_URL}/internal/backend_users/sendgrid/unsubscribes/${params.id}`,
            options: { method: "DELETE" },
          };
        case "Group Unsubscribe":
          return {
            url: `${API_URL}/internal/sendgrid/unsubscribes/group/${params.id}/${params.group_id}`,
            options: { method: "DELETE" },
          };
      }
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingSendgrid = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      if (params?.meta?.activity) {
        return { data: Object.assign(json, { id: json.msg_id }) };
      }
      return { data: Object.assign(json, { id: json.statuses }) };
    case GET_LIST:
      const messages = json.messages;
      return {
        data: messages.map((x) => Object.assign(x, { id: x.msg_id })),
        total: messages.length,
      };
    case DELETE:
      return {
        data: Object.assign(json, { id: params.id }),
      };
  }
};

const incomingCustomers = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.uuid })),
        total: json.length,
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.uuid }),
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: json.uuid }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingDriveAxleUsers = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      var new_json = [json]; // we just use the regular get scanning_users/:email endpoint here, so convert it into a list to pretend its like a search
      return {
        data: new_json.map((x) => Object.assign(x, { id: x.email })),
        total: new_json.length,
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.email }),
      };
    case UPDATE:
      return { data: Object.assign(json, { id: params.id }) };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingDocumentFormats = (json, type, params) => {
  switch (type) {
    case GET_MANY:
      return {
        data: json.map((x) => ({
          ...x,
          id: x.id,
          supports_full_color: x.supports_full_color,
        })),
        total: json.length,
      };
    case GET_LIST:
      const updatedData = json.map((x) => ({
        ...x,
        id: x.id,
        supports_full_color: !x.supports_full_color,
      }));
      return {
        data: updatedData,
        total: updatedData.length,
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.uuid }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};
const incomingDocuments = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      for (let i = 0; i < json.length; i++) {
        json[i]["#"] = i + 1;
      }
      return {
        data: json.map((x) => Object.assign(x, { id: x.document_uuid })),
        total: json.length,
      };
    case GET_ONE:
      return { data: Object.assign(json, { id: json.document_uuid }) };
    case UPDATE:
      return {
        data: Object.assign(json, { id: params.id }),
        total: 1,
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingCharge = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return {
        data: json.map((x) => Object.assign(x, { id: x.uuid })),
        total: json.length,
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.uuid }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingStripeCharges = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      json.map((x) => Object.assign(x, { created: new Date(x.created * 1000).toLocaleString() }));
      return {
        data: json.map((x) => Object.assign(x, { id: x.id })),
        total: json.length,
      };
    case GET_ONE:
      if (json.created) Object.assign(json, { created: new Date(json.created * 1000).toLocaleString() });
      return {
        data: Object.assign(json, { id: json.id }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingStripeSubscriptions = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      json.map((x) => Object.assign(x, { created: new Date(x.created * 1000).toLocaleString() }));
      json = [json];
      return {
        data: json.map((x) => Object.assign(x, { id: x.id })),
        total: json.length,
      };
    case GET_ONE:
      if (json.created) Object.assign(json, { created: new Date(json.created * 1000).toLocaleString() });
      return {
        data: Object.assign(json, { id: json.id }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingClientGroups = (type, params) => {
  switch (type) {
    case GET_LIST:
      return { url: `${API_URL}/internal/client_groups` };
    case GET_MANY:
      return { url: `${API_URL}/internal/client_groups` };
    default:
      return baseOutgoingRequest(type, "internal/client_groups", params);
  }
};

const incomingApps = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      let ios_here_permissions = {};
      let android_here_permissions = {};

      try {
        if (json.ios_here_key) ios_here_permissions = decryptHereKey(json.ios_here_key);
      } catch (e) {
        e.name = "Error decrypting iOS HERE KEY";
        Sentry.captureException(e, {
          level: "warning",
          tags: { type: "GET_ONE", resource: "apps" },
        });
      }
      try {
        if (json.android_here_key) android_here_permissions = decryptHereKey(json.android_here_key);
      } catch (e) {
        e.name = "Error decrypting Android HERE KEY";
        Sentry.captureException(e, {
          level: "warning",
          tags: { type: "GET_ONE", resource: "apps" },
        });
      }

      return {
        data: Object.assign(json, {
          ios_environment_properties: json.ios_environment_properties
            ? JSON.stringify(json.ios_environment_properties)
            : null,
          android_environment_properties: json.android_environment_properties
            ? JSON.stringify(json.android_environment_properties)
            : null,
          ios_here_permissions: ios_here_permissions,
          android_here_permissions: android_here_permissions,
          android_signing_fingerprints: json.android_signing_fingerprints,
          id: json.client_key,
        }),
      };
    case GET_MANY_REFERENCE:
      return {
        data: json.map((x) => Object.assign(x, { id: x.client_key })),
        total: json.length,
      };
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.client_key })),
        total: json.length,
      };
    case GET_LIST:
      if (params.meta) {
        return {
          data: json.map((x) => Object.assign(x, { id: x.pipeline_uuid })),
          total: json.length,
        };
      }
      if (params.filter && "q" in params.filter) {
        const query = params.filter.q.toLowerCase();
        json = json.filter(
          (item) => item.client_key.toLowerCase().includes(query) || item.name.toLowerCase().includes(query)
        );
      } else {
        json = filterResources(json, params.filter);
      }
      return {
        data: json.map((x) => Object.assign(x, { id: x.client_key })),
        total: json.length,
      };
    case GET_MANY_REFERENCE:
      return {
        data: json.map((x) => Object.assign(x, { id: x.client_key })),
        total: json.length,
      };
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.client_key })),
        total: json.length,
      };
    case CREATE:
      if (params?.switcher) {
        return {
          data: Object.assign(json, { id: params.destinationKey }),
        };
      }
      var newJson = Object.assign(json, { id: json.key });
      return { data: newJson };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingAssets = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return {
        data: Object.assign(json, { id: params.handle }),
      };
    case CREATE:
      return {
        data: Object.assign(json, { id: params.handle }),
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: params.handle }),
      };
  }
};

const incomingAppAssets = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return {
        data: json,
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.id }),
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: json.url }),
        total: 1,
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingAppBranding = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return {
        data: Object.assign(json, { id: uuid() }),
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: uuid() }),
      };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingAppAssets = (type, params) => {
  switch (type) {
    case GET_LIST:
      return { url: `${API_URL}/internal/clients/${params.id}/assets` };
    case GET_ONE:
      return { url: `${API_URL}/internal/clients/${params.id}/assets` };
    case UPDATE:
      return params.getPresigned
        ? {
            url: `${API_URL}/internal/clients/${params.id}/assets/${params.appAssetName}`,
            options: {
              method: "PUT",
            },
          }
        : {
            url: params.id,
            options: {
              method: "PUT",
              body: params.body,
            },
          };
    default:
      return baseOutgoingRequest(type, `${API_URL}/internal/clients/${params.id}/assets`, params);
  }
};

const outgoingAppBranding = (type, resourcePath, params) => {
  switch (type) {
    case UPDATE:
      return {
        url: `${PLATFORM_API_URL}/${resourcePath}`,
        options: {
          method: "PATCH",
          headers: {
            "eleos-admin-client-key": params.id,
          },
          body: JSON.stringify(params.body),
        },
      };
    default:
      return outgoingClientResource(type, resourcePath, params);
  }
};

const incomingAltDashboardLogos = (json, type, params) => {
  switch (type) {
    case CREATE:
      return {
        data: Object.assign(json, { id: params.filter.filename }),
        total: 1,
      };
    case GET_MANY_REFERENCE:
      const data = json.map((x) => {
        return Object.assign({}, x, { id: x.filename });
      });
      return {
        data: data,
        total: data.length,
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.id }),
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: params.filter.url }),
        total: 1,
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingDocFormatAssociations = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.document_format_id })),
        total: json.length,
      };
    case GET_MANY_REFERENCE:
      return {
        data: json.map(x, (i) => Object.assign(x, { id: i })),
        total: json.length,
      };
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.id }),
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: json.document_format_id }),
        total: 1,
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingDocFormatAssociations = (type, params) => {
  switch (type) {
    case GET_LIST:
      return { url: `${API_URL}/internal/clients/document_type_format_associations/${params.id}` };
    case GET_ONE:
      return { url: `${API_URL}/internal/clients/document_type_format_associations/${params.id}` };
    case UPDATE:
      return {
        url: `${API_URL}/internal/clients/document_type_format_associations/${params.id}`,
        options: {
          method: "PATCH",
          body: params.body,
        },
      };
    default:
      return baseOutgoingRequest(
        type,
        `${API_URL}/internal/clients/document_type_format_associations/${params.id}`,
        params
      );
  }
};

const outgoingAuditLog = (type, resource, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      const urlString = `${API_URL}/internal/auditlog/customers/${params.filter.uuid}`;
      return { url: urlString };
    default:
      return baseOutgoingRequest(type, resource, params);
  }
};

const incomingGPXFiles = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return { data: Object.assign(json, { id: params.filter.url }) };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.url })),
        total: json.length,
      };
    case UPDATE:
      const id = params.body ? params.filter.url : json.url;
      return {
        data: Object.assign(json, { id: id }),
        total: 1,
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};
const outgoingGPXFiles = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      return { url: params.filter.url };
    case GET_LIST:
      var urlString = `${API_URL}/internal/demo_service_gpx_files`;
      return { url: urlString };
    case UPDATE:
      return params.body
        ? { url: params.filter.url, options: { method: "PUT", body: params.body } }
        : { url: `${API_URL}/internal/demo_service_gpx_files/${params.filter.fileName}`, options: { method: "PUT" } };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingAuditLog = (json, type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return {
        data: json.map((entry) => ({
          ...entry,
          uuid: entry.id,
        })),
        total: json.length,
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingChangelog = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      var urlString = `${API_URL}/internal/changelog/${params.filter.from}/${params.id}`;
      return { url: urlString };
    case GET_MANY_REFERENCE:
      var urlString = `${API_URL}/internal/changelog/${params.filter.from}/${params.id}`;
      return { url: urlString };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingChangelog = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return {
        data: Object.assign(json, { id: json.uuid }),
      };
    case GET_MANY_REFERENCE:
      return {
        data: json.map((x) => Object.assign(x, { id: x.uuid })),
        total: json.length,
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const incomingPermissions = (json, type, params) => {
  switch (type) {
    case CREATE:
      return {
        data: Object.assign(json, { id: params.data.group }),
        total: 1,
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: params.data.group }),
        total: 1,
      };
    case GET_LIST:
      const isStringsArray = (json) => Array.isArray(json) && json.every((i) => typeof i === "string");

      if (isStringsArray(json)) {
        const data = json.map((resource, i) => ({ id: i, value: resource }));
        return {
          data: data,
          total: data.length,
        };
      }

      const listJson = [];
      for (const [key, value] of Object.entries(json)) {
        let resources = [];

        if (value) {
          for (const [k2, v2] of Object.entries(value)) {
            resources.push({ id: k2, perms: v2 });
          }
        }

        listJson.push({ id: key, permissions: resources.length ? resources : null });
      }

      return {
        data: listJson,
        total: listJson.length,
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingAltDashboardLogo = (type, params) => {
  switch (type) {
    case GET_MANY_REFERENCE:
      return { url: `${API_URL}/internal/clients/${params.id}/alt_dashboard_logos` };
    case GET_ONE:
      return { url: `${API_URL}/internal/clients/${params.id}/alt_dashboard_logos` };
    case CREATE:
      return {
        url: `${API_URL}/internal/clients/${params.id}/assets/alt_dashboard_logos/${params.filter.filename}`,
        options: {
          method: "PUT",
        },
      };
    case UPDATE:
      return {
        url: params.filter.url,
        options: {
          method: "PUT",
          body: params.body,
          headers: { "x-amz-acl": "public-read" },
        },
      };
    default:
      return baseOutgoingRequest(type, `${API_URL}/internal/clients/${params.id}/alt_dashboard_logos`, params);
  }
};

const outgoingPermissions = (type, resourcePath, params) => {
  switch (type) {
    case CREATE:
      if (params?.meta === "createGroup") {
        return {
          url: `${API_URL}/internal/permissions/groups/${params.data.group}`,
          options: { method: "POST" },
        };
      } else {
        return {
          url: `${API_URL}/internal/permissions/`,
          options: { method: "POST", body: JSON.stringify(params.data) },
        };
      }
    case UPDATE:
      const { useActive, ...data } = params.data || {};
      const url = useActive ? `${API_URL}/internal/permissions/active` : `${API_URL}/internal/permissions/update`;
      return {
        url: url,
        options: { method: "PATCH", body: JSON.stringify(data) },
      };
    case GET_ONE:
      return {
        url: `${API_URL}/internal/permissions/${params.id}`,
      };
    case GET_LIST:
      if (params?.meta === "getResources") {
        return {
          url: `${API_URL}/internal/permissions/resources/all`,
        };
      }
      const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      if (params.filter && !("q" in params.filter)) {
        if (params.filter.group_name != null && emailRegex.test(params.filter.group_name)) {
          return {
            url: `${API_URL}/internal/permissions/${params.filter.group_name}`,
          };
        } else {
          return {
            url: `${API_URL}/internal/permissions/all`,
          };
        }
      }
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingRetentionPolicies = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      var urlString = `${API_URL}/internal/retention_policies/${params.id}`;
      return { url: urlString };
    case UPDATE:
      var payload = selectModifiedValues(params.previousData, params.data);
      payload = payload == null ? {} : payload;
      return {
        url: `${API_URL}/internal/retention_policies/${params.id}`,
        options: { method: "PATCH", body: JSON.stringify(payload) },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingRetentionPolicies = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return { data: Object.assign(json, { id: params.id }) };
    case UPDATE:
      return { data: { id: params.id } };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingScanningUserDeleteDrafts = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      var urlString = `${API_URL}/${resourcePath}/${params.meta.email}/drafts`;
      return { url: urlString };
    case UPDATE:
      var urlString = `${API_URL}/${resourcePath}/${params.meta.email}/drafts/${params.id}`;
      return {
        url: urlString,
        options: { method: "PATCH" },
      };
    case CREATE:
      var urlString = `${API_URL}/${resourcePath}/${params.data.email}`;
      return {
        url: urlString,
        options: { method: "DELETE" },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingScanningUserDeleteDrafts = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      if (params.filter && !("q" in params.filter)) {
        json = filterResources(json, params.filter);
      }
      return {
        data: json,
        total: json.length,
      };
    default:
      return {
        data: { id: params.id },
      };
  }
};

const incomingAppEditDrafts = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      if (params.filter && !("q" in params.filter)) {
        json = filterResources(json, params.filter);
      }
      return {
        data: json,
        total: json.length,
      };
    default:
      return {
        data: { id: params.id },
      };
  }
};

const outgoingAppEditDrafts = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      var urlString = `${API_URL}/${resourcePath}/${params.filter.id}`;
      return { url: urlString };
    case UPDATE:
      // approve draft
      if (params.meta?.clientKey) {
        var urlString = `${API_URL}/${resourcePath}/${params.meta.clientKey}/${params.id}`;
        return {
          url: urlString,
          options: { method: "PATCH" },
        };
      }
      // create draft
      var urlString = `${API_URL}/${resourcePath}/${params.id}`;
      var updated_values = selectModifiedValues(params.previousData, params.data);
      return {
        url: urlString,
        options: { method: "PATCH", body: JSON.stringify(updated_values) },
      };

    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const outgoingBackendUserInviteDrafts = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      var urlString = `${API_URL}/${resourcePath}/draft`;
      return { url: urlString };
    case UPDATE:
      var urlString = `${API_URL}/${resourcePath}/draft/${params.id}`;
      return {
        url: urlString,
        options: { method: "PATCH" },
      };
    case CREATE:
      var urlString = `${API_URL}/${resourcePath}/${params.meta.clientKey}`;
      return {
        url: urlString,
        options: {
          method: "POST",
          body: JSON.stringify(params.data),
        },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBackendUserInviteDrafts = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      if (params.filter && !("q" in params.filter)) {
        json = filterResources(json, params.filter);
      }
      return {
        data: json,
        total: json.length,
      };
    default:
      return {
        data: { id: params.id },
      };
  }
};

const outgoingBackendUserMigrationDrafts = (type, resourcePath, params) => {
  switch (type) {
    case CREATE:
      var urlString = "";
      if (params.data.account_type === "client") {
        urlString = `${API_URL}/${resourcePath}/${params.data.email}/client/${params.data.key}`;
      } else if (params.data.account_type === "customer") {
        urlString = `${API_URL}/${resourcePath}/${params.data.email}/customer/${params.data.uuid}`;
      }
      return {
        url: urlString,
        options: { method: "POST" },
      };
    case GET_LIST:
      var urlString = `${API_URL}/${resourcePath}/drafts`;
      return { url: urlString };
    case UPDATE:
      var urlString = `${API_URL}/${resourcePath}/drafts/${params.id}`;
      return {
        url: urlString,
        options: { method: "PATCH" },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBackendUserMigrationDrafts = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      if (params.filter && !("q" in params.filter)) {
        json = filterResources(json, params.filter);
      }
      return {
        data: json,
        total: json.length,
      };
    default:
      return {
        data: { id: params.id },
      };
  }
};

const outgoingIntegrationMetadataFormats = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      var urlString = "";
      if (params.meta.account_type === "client") {
        urlString = `${API_URL}/${resourcePath}/client/${params.id}`;
      } else if (params.meta.account_type === "customer") {
        urlString = `${API_URL}/${resourcePath}/customer/${params.id}`;
      }

      return { url: urlString };
    case GET_LIST:
      var urlString = `${API_URL}/${resourcePath}`;
      return { url: urlString };
    case GET_MANY:
      var urlString = `${API_URL}/${resourcePath}`;
      return { url: urlString };
    case UPDATE:
      var urlString = "";
      if (params.data.account_type === "client") {
        urlString = `${API_URL}/${resourcePath}/client/${params.id}`;
      } else if (params.data.account_type === "customer") {
        urlString = `${API_URL}/${resourcePath}/customer/${params.id}`;
      }

      return {
        url: urlString,
        options: {
          method: "PATCH",
          body: JSON.stringify({ integration_metadata_format: params.data.integration_metadata_format }),
        },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingIntegrationMetadataFormats = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return { data: Object.assign(json, { id: json.name }) };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.name })),
        total: json.length,
      };
    case GET_MANY:
      return {
        data: json.map((x) => Object.assign(x, { id: x.name })),
      };
    case UPDATE:
      return {
        data: Object.assign(json, { id: json.name }),
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingEmailVerifications = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      var urlString = `${API_URL}/internal/backend_users/${params.filter.email}/verifications`;
      return { url: urlString };
    case CREATE:
      return {
        url: `${API_URL}/${resourcePath}/${params.data.email}`,
        options: { method: "PUT", body: JSON.stringify({ message: params.data.message }) },
      };
    default:
      return baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingEmailVerifications = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.verification_sent_at })),
        total: json.length,
      };
    case CREATE:
      return {
        data: { id: params.data.email },
      };
    default:
      return baseIncomingRequest(json, type, params);
  }
};

const outgoingCustomerUnsubscribeDrafts = (type, resourcePath, params) => {
  const iso_date = params.data?.unsubscribeDate ? new Date(params.data.unsubscribeDate).toISOString() : null;
  switch (type) {
    case CREATE:
      return {
        url: `${API_URL}/${resourcePath}/${params.data.uuid}`,
        options: {
          method: "POST",
          body: JSON.stringify({
            preserve_backend_user_email: params.data.preserve_backend_user_email,
            cancellation_date: iso_date,
          }),
        },
      };
    case GET_LIST:
      return { url: `${API_URL}/${resourcePath}/drafts` };
    case UPDATE:
      return {
        url: `${API_URL}/${resourcePath}/drafts/${params.id}`,
        options: { method: "PATCH" },
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingCustomerUnsubscribeDrafts = (json, type, params) => {
  switch (type) {
    case CREATE:
      return { data: { id: params.data.uuid } };
    case GET_LIST:
      if (params.filter && !("q" in params.filter)) {
        json = filterResources(json, params.filter);
      }
      return {
        data: json,
        total: json.length,
      };
    case UPDATE:
      return { data: { id: params.id } };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingCustomerKeyRotationDrafts = (type, resourcePath, params) => {
  switch (type) {
    case CREATE:
      return {
        url: `${API_URL}/${resourcePath}/${params.data.uuid}`,
        options: { method: "POST" },
      };
    case GET_LIST:
      return { url: `${API_URL}/${resourcePath}/drafts` };
    case UPDATE:
      return {
        url: `${API_URL}/${resourcePath}/drafts/${params.id}`,
        options: { method: "PATCH" },
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingCustomerKeyRotationDrafts = (json, type, params) => {
  switch (type) {
    case CREATE:
      return { data: { id: params.data.uuid } };
    case GET_LIST:
      if (params.filter && !("q" in params.filter)) {
        json = filterResources(json, params.filter);
      }
      return {
        data: json,
        total: json.length,
      };
    case UPDATE:
      return { data: { id: params.id } };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingAdministratorGroups = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      return { url: `${API_URL}/${resourcePath}` };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingAdministratorGroups = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      let data = Object.keys(json).map((key) => Object.assign({ id: key }, json[key]));
      return {
        data: data,
        total: data.length,
      };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingIssueReportDownload = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      return { url: params.url };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingIssueReportDownload = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return { data: Object.assign(json, { id: json.url }) };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingIssueReports = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
      };
    case GET_LIST:
      return {
        url: `${API_URL}/${resourcePath}/${params.meta.client_key}/${params.meta.username}`,
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingIssueReports = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return { data: Object.assign(json, { id: json.url }) };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.uuid, report_time: x.report_time })),
        total: json.length,
      };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingTripRoutes = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      return {
        url: `${API_URL}/${resourcePath}/${params.filter.clientKey}/${params.filter.username}/?start=${params.filter.start}&end=${params.filter.end}`,
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingTripRoutes = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      return {
        data: json,
        total: json.length,
      };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingTripPlans = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      return {
        url: `${API_URL}/api/v1/${resourcePath}/${params.filter.username}/${params.filter.loadId}/${params.filter.uuid}`,
      };
    case GET_LIST:
      return {
        url: `${API_URL}/api/v1/${resourcePath}/${params.filter.username}/${params.filter.loadId}`,
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingTripPlans = (json, type, params) => {
  // Ensure json is parsed if it arrives as a string
  if (typeof json === "string") {
    json = JSON.parse(json);
  }
  switch (type) {
    case GET_ONE:
      return { data: Object.assign(json, { id: json.uuid }) };
    case GET_LIST:
      return {
        data: json.map((x) => Object.assign(x, { id: x.uuid })),
        total: json.length,
      };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingRoutes = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      if (params.filter?.type === "diff_gen") {
        return {
          url: `${API_URL}/internal/routes/${params.id}?chunk_size=${params.filter.chunkSize}&chunking_type=${params.filter.chunkingType}`,
        };
      }
      if (params.filter?.type === "bad_chunk") {
        return {
          url: `${API_URL}/internal/routes/${params.id}?chunk_size=${params.filter.chunkSize}`,
        };
      }
      return {
        url: `${API_URL}/${resourcePath}/${params.id}?chunk_size=50&chunking_type=two_step`,
      };
    case GET_LIST:
      return {
        url: `${API_URL}/internal/routes/?client_key=${params.id}&username=${params.filter.username}&load_reference=${params.filter.loadRef}`,
        options: {
          mode: "cors",
          headers: { "Content-Type": "application/json" },
        },
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};
const incomingRoutes = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return { data: Object.assign(json, { id: params.id }) };
    case GET_LIST:
      return {
        data: json,
        total: json.length,
      };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingBackendUserRoleDrafts = (type, resourcePath, params) => {
  switch (type) {
    case CREATE:
      return {
        url: `${API_URL}/internal/backend_users/${params.meta.email}/roles/${params.meta.role}`,
        options: {
          method: "POST",
        },
      };
    case DELETE:
      return {
        url: `${API_URL}/internal/backend_users/${params.meta.email}/roles/${params.meta.role}`,
        options: {
          method: "DELETE",
        },
      };
    case GET_LIST:
      return { url: `${API_URL}/internal/backend_users/${params.meta.email}/roles/drafts` };
    case UPDATE:
      return {
        url: `${API_URL}/internal/backend_users/${params.meta.email}/roles/drafts/${params.id}`,
        options: { method: "PATCH" },
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBackendUserRoleDrafts = (json, type, params) => {
  switch (type) {
    case CREATE:
    case DELETE:
      return { data: { id: json.message } };
    case GET_LIST:
      if (params.filter && !("q" in params.filter)) {
        json = filterResources(json, params.filter);
      }
      return {
        data: json,
        total: json.length,
      };
    case UPDATE:
      return { data: { id: params.id } };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingTriggerBuilds = (type, resourcePath, params) => {
  switch (type) {
    case UPDATE:
      let url = `${API_URL}/api/v1/apps/${params.body.tag}/${params.body.platform}`;
      const query = params.query ? new URLSearchParams(params.query).toString() : "";
      if (query) url = `${url}?${query}`;
      return {
        url: url,
        options: { method: "PUT" },
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingTriggerBuilds = (json, type, params) => {
  switch (type) {
    case UPDATE:
      let composite = `${params.body.platform}/${params.body.tag}`;
      return {
        data: Object.assign(json, { id: composite }),
      };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingArchivedBuilds = (type, resourcePath, params) => {
  switch (type) {
    case CREATE:
      return {
        url: `${API_URL}/internal/apps/${params.meta.clientKey}/${params.meta.platform}/archived_builds`,
        options: { method: "POST", body: JSON.stringify(params.data) },
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingArchivedBuilds = (json, type, params) => {
  switch (type) {
    case CREATE:
      return { data: { ...json, id: json.pipeline_uuid } };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingHERECalls = (type, resourcePath, params) => {
  switch (type) {
    case UPDATE:
      const requestBody = params.data;
      return {
        url: params.userInput
          ? params.url
          : `https://router.hereapi.com/v8/import?apikey=${process.env.REACT_APP_HERE_MAP_API_KEY}&return=polyline,summary,actions,instructions&transportMode=truck`,
        options: {
          method: "POST",
          body: params.userInput ? params.body : JSON.stringify({ trace: requestBody }),
        },
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingHERECalls = (json, type, params) => {
  switch (type) {
    case UPDATE:
      return { data: Object.assign(json, { id: params.id }) };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingAppReleases = (type, resourcePath, params) => {
  switch (type) {
    case GET_LIST:
      return { url: `${API_URL}/${resourcePath}` };
    case UPDATE:
    case CREATE:
      let tag = params.id ? params.id : params.data["release_name"];
      return {
        url: `${API_URL}/${resourcePath}/${tag}`,
        options: {
          method: "PUT",
          body: JSON.stringify(params.data),
        },
      };

    case DELETE:
      return {
        url: `${API_URL}/${resourcePath}/${params.id}`,
        options: {
          method: "DELETE",
        },
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingAppReleases = (json, type, params) => {
  switch (type) {
    case GET_LIST:
      let data = json.map((x) => Object.assign(x, { id: x.release_name }));
      return { data: data, total: json.length };
    case UPDATE:
    case CREATE:
      return { data: Object.assign({ id: params.id }) };
    case DELETE:
      return {
        data: Object.assign(json, { id: params.id }),
      };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingBulkTriggers = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      return { url: `${LAMBDA_URL}?composite_tag=${params.id}` };
    case UPDATE:
      return {
        url: LAMBDA_URL,
        options: {
          method: "POST",
          body: JSON.stringify(params.meta),
        },
      };
    default:
      baseOutgoingRequest(type, resourcePath, params);
  }
};

const incomingBulkTriggers = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return { data: Object.assign(json, { id: params.id }) };
    case UPDATE:
      return { data: Object.assign(json, { id: params.id }) };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingPluggablePatching = (type, resource, params) => {
  switch (type) {
    case CREATE:
      return {
        url: `${API_URL}/internal/pluggable_patch/${params.id}`,
        options: {
          method: "POST",
          body: JSON.stringify(params.body),
        },
      };
    case UPDATE:
      return {
        url: `${API_URL}/internal/test_pluggable_patch/${params.id}`,
        options: {
          method: "PUT",
          body: JSON.stringify(params.body),
        },
      };
    default:
      baseOutgoingRequest(type, resource, params);
  }
};
const incomingPluggablePatching = (json, type, params) => {
  switch (type) {
    case CREATE:
      return { data: { id: json.uuid } };
    case UPDATE:
      return { data: Object.assign(json, { id: params.id }) };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingMessages = (type, resource, params) => {
  switch (type) {
    case UPDATE:
      return {
        url: `${API_URL}/api/v1/messages/${params.id}`,
        options: {
          method: "PUT",
          body: params.body,
        },
      };
    default:
      baseOutgoingRequest(type, resource, params);
  }
};
const incomingMessages = (json, type, params) => {
  switch (type) {
    case UPDATE:
      return { data: Object.assign(json, { id: params.id }) };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingPlatformUsers = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      return { url: `${API_URL}/${resourcePath}/${params.clientKey}/${params.username}` };
    case UPDATE:
      return {
        url: `${API_URL}/${resourcePath}/${params.clientKey}/${params.username}`,
        options: {
          method: "PATCH",
          body: JSON.stringify(params.body),
        },
      };
    default:
      baseOutgoingRequest(json, type, params);
  }
};
const incomingPlatformUsers = (json, type, params) => {
  switch (type) {
    case GET_ONE:
    case UPDATE:
      return { data: Object.assign(json, { id: params.username }) };
    default:
      baseIncomingRequest(json, type, params);
  }
};

const outgoingPatches = (type, resourcePath, params) => {
  switch (type) {
    case GET_ONE:
      return { url: `${API_URL}/internal/pluggable_patch/${params.id}` };
    case GET_LIST:
      return { url: `${API_URL}/internal/${resourcePath}` };
    case UPDATE:
      return {
        url: `${API_URL}/internal/pluggable_patch/${params.id}`,
        options: {
          method: "PATCH",
          body: JSON.stringify(params.data),
        },
      };
    default:
      baseOutgoingRequest(json, type, params);
  }
};
const incomingPatches = (json, type, params) => {
  switch (type) {
    case GET_ONE:
      return { data: Object.assign(json, { id: params.id }) };
    case GET_LIST:
      let data = json.map((x) => Object.assign(x, { id: x.uuid }));
      return { data: data, total: json.length };
    case UPDATE:
      return { data: Object.assign(json, { id: json.uuid }) };
    default:
      baseIncomingRequest(json, type, params);
  }
};

/**
 * @param {String} type One of the constants appearing at the top of this file, e.g. "UPDATE"
 * @param {String} resource Name of the resource to fetch, e.g. "posts"
 * @param {Object} params The Data Provider request params, depending on the type
 * @returns {Object} { url, options } The HTTP request parameters
 */
const convertDataProviderRequestToHTTP = (type, resource, params) => {
  switch (resource) {
    case "clients":
      return outgoingClients(type, params);
    case "app_assets":
      return outgoingAppAssets(type, params);
    case "app_branding":
      return outgoingAppBranding(type, resource, params);
    case "alt_dashboard_logos":
      return outgoingAltDashboardLogo(type, params);
    case "client_groups":
      return outgoingClientGroups(type, params);
    case "billing_accounts":
      return outgoingBillingAccounts(type, `internal/${resource}`, params);
    case "billing_accounts_v2":
      return outgoingBillingAccounts(type, `internal/${resource}`, params);
    case "contract_structures":
      return outgoingBillingContractStructures(type, `internal/${resource}`, params);
    case "contract_structure_products":
      return outgoingBillingContractStructureProducts(type, `internal/${resource}`, params);
    case "per_load_billing_structure":
      return outgoingPerLoadBillingStructure(type, `internal/${resource}`, params);
    case "account_contracts":
      return outgoingBillingAccountContracts(type, `internal/${resource}`, params);
    case "price_sheets":
      return outgoingBillingPriceSheets(type, `internal/${resource}`, params);
    case "price_sheet_items":
      return outgoingBillingPriceSheetItems(type, `internal/${resource}`, params);
    case "products":
      return outgoingBillingProducts(type, `internal/${resource}`, params);
    case "customers":
      return outgoingCustomers(type, `internal/${resource}`, params);
    case "scanning_users":
      return outgoingDriveAxleUsers(type, `internal/${resource}`, params);
    case "documents":
      return outgoingDocuments(type, `internal/${resource}`, params);
    case "backend_users":
      return outgoingBackendUser(type, `${resource}`, params);
    case "charges":
      return outgoingCharge(type, resource, params);
    case "stripe_charges":
      return outgoingStripeCharges(type, resource, params);
    case "stripe_subscriptions":
      return outgoingStripeSubscriptions(type, resource, params);
    case "document_types":
      return outgoingDocumentFormats(type, resource, params);
    case "apps":
      return outgoingApps(type, `internal/${resource}`, params);
    case "apple_accounts":
      return baseOutgoingRequest(type, `internal/${resource}`, params);
    case "changelog":
      return outgoingChangelog(type, `${resource}`, params);
    case "auditlog":
      return outgoingAuditLog(type, `${resource}`, params);
    case "retention_policies":
      return outgoingRetentionPolicies(type, `internal/${resource}`, params);
    case "backend_user_migration_drafts":
      return outgoingBackendUserMigrationDrafts(type, "internal/migrate_backend_user", params);
    case "backend_user_invite_drafts":
      return outgoingBackendUserInviteDrafts(type, "internal/clients/invite", params);
    case "scanning_user_delete_drafts":
      return outgoingScanningUserDeleteDrafts(type, "internal/scanning_users", params);
    case "app_edit_drafts":
      return outgoingAppEditDrafts(type, "internal/drafts/apps", params);
    case "integration_metadata_formats":
      return outgoingIntegrationMetadataFormats(type, `internal/${resource}`, params);
    case "email_verifications":
      return outgoingEmailVerifications(type, "internal/backend_users/verification", params);
    case "customer_unsubscribe_drafts":
      return outgoingCustomerUnsubscribeDrafts(type, "internal/customers/unsubscribe", params);
    case "administrator_groups":
      return outgoingAdministratorGroups(type, `internal/${resource}`, params);
    case "customer_key_rotation_drafts":
      return outgoingCustomerKeyRotationDrafts(type, "internal/customers/rotate_key", params);
    case "internal_permissions":
      return outgoingPermissions(type, "internal/permissions/all", params);
    case "gpx_files":
      return outgoingGPXFiles(type, `internal/demo_service_gpx_files`, params);
    case "document_type_format_associations":
      return outgoingDocFormatAssociations(type, params);
    case "issue_report":
      return outgoingIssueReports(type, `internal/issue_report`, params);
    case "issue_report_download":
      return outgoingIssueReportDownload(type, `${resource}`, params);
    case "trip_routes":
      return outgoingTripRoutes(type, `internal/${resource}`, params);
    case "trip_plans":
      return outgoingTripPlans(type, `${resource}`, params);
    case "routes":
      return outgoingRoutes(type, `internal/${resource}`, params);
    case "sendgrid":
      return outgoingSendgrid(type, `${resource}`, params);
    case "backend_user_role_drafts":
      return outgoingBackendUserRoleDrafts(type, `${resource}`, params);
    case "trigger_builds":
      return outgoingTriggerBuilds(type, resource, params);
    case "archived_builds":
      return outgoingArchivedBuilds(type, resource, params);
    case "here_call":
      return outgoingHERECalls(type, resource, params);
    case "app_releases":
      return outgoingAppReleases(type, `internal/${resource}`, params);
    case "bulk_triggers":
      return outgoingBulkTriggers(type, resource, params);
    case "patches":
      return outgoingPluggablePatching(type, resource, params);
    case "messages":
      return outgoingMessages(type, resource, params);
    case "platform_users":
      return outgoingPlatformUsers(type, `internal/${resource}`, params);
    case "pluggable_patch":
      return outgoingPatches(type, resource, params);
    default:
      return outgoingClientResource(type, `${resource}`, params);
  }
};

/**
 * @param {Response} response the raw HTTP response from fetch()
 * @param {String} type One of the constants appearing at the top of this file, e.g. "UPDATE"
 * @param {String} resource Name of the resource to fetch, e.g. "posts"
 * @param {Object} params The Data Provider request params, depending on the type
 * @returns {Object} Data Provider response
 */
const convertHTTPResponseToDataProvider = async (response, type, resource, params) => {
  switch (resource) {
    case "clients":
      return incomingClients(await response.json(), type, params);
    case "client_groups":
      return incomingClientGroups(await response.json(), type, params);
    case "apps":
      return incomingApps(await response.json(), type, params);
    case "apple_accounts":
      return incomingAppleAccounts(await response.json(), type, params);
    case "billing_accounts":
    case "billing_accounts_v2":
      return incomingBillingAccounts(await response.json(), type, params);
    case "contract_structures":
      return incomingBillingContractStructures(await response.json(), type, params);
    case "contract_structure_products":
      return incomingBillingContractStructureProducts(await response.json(), type, params);
    case "per_load_billing_structure":
      return incomingPerLoadBillingStructure(await response.json(), type, params);
    case "account_contracts":
      return incomingBillingAccountContracts(await response.json(), type, params);
    case "price_sheets":
      return incomingBillingPriceSheets(await response.json(), type, params);
    case "price_sheet_items":
      return incomingBillingPriceSheetItems(await response.json(), type, params);
    case "products":
      return incomingBillingProducts(await response.json(), type, params);
    case "customers":
      return incomingCustomers(await response.json(), type, params);
    case "scanning_users":
      return incomingDriveAxleUsers(await response.json(), type, params);
    case "documents":
      return incomingDocuments(await response.json(), type, params);
    case "forms":
      if (type === "UPDATE" && params.body) {
        return incomingClientResource([], type, resource, params);
      }
      return incomingClientResource(await response.json(), type, resource, params);
    case "menus":
    case "dashboards":
    case "actions":
    case "screens":
      return incomingClientResource(await response.json(), type, resource, params);
    case "app_assets":
      if (type === "UPDATE" && params.body) {
        return incomingAppAssets([], type, params);
      }
      return incomingAppAssets(await response.json(), type, params);
    case "assets":
      return incomingAssets(await response.json(), type, params);
    case "app_branding":
      return incomingAppBranding(await response.json(), type, params);
    case "alt_dashboard_logos":
      if (type === "UPDATE" && params.body) {
        return incomingAltDashboardLogos([], type, params);
      }
      return incomingAltDashboardLogos(await response.json(), type, params);
    case "backend_users":
      return incomingBackendUsers(await response.json(), type, params);
    case "charges":
      return incomingCharge(await response.json(), type, params);
    case "stripe_charges":
      return incomingStripeCharges(await response.json(), type, params);
    case "stripe_subscriptions":
      return incomingStripeSubscriptions(await response.json(), type, params);
    case "document_types":
      return incomingDocumentFormats(await response.json(), type, params);
    case "errors":
      return incomingClientResource(await response.json(), type, resource, params);
    case "changelog":
      return incomingChangelog(await response.json(), type, params);
    case "auditlog":
      return incomingAuditLog(await response.json(), type, params);
    case "retention_policies":
      return incomingRetentionPolicies(await response.json(), type, params);
    case "backend_user_migration_drafts":
      return incomingBackendUserMigrationDrafts(await response.json(), type, params);
    case "backend_user_invite_drafts":
      return incomingBackendUserInviteDrafts(await response.json(), type, params);
    case "scanning_user_delete_drafts":
      return incomingScanningUserDeleteDrafts(await response.json(), type, params);
    case "app_edit_drafts":
      return incomingAppEditDrafts(await response.json(), type, params);
    case "integration_metadata_formats":
      return incomingIntegrationMetadataFormats(await response.json(), type, params);
    case "email_verifications":
      return incomingEmailVerifications(await response.json(), type, params);
    case "customer_unsubscribe_drafts":
      return incomingCustomerUnsubscribeDrafts(await response.json(), type, params);
    case "administrator_groups":
      return incomingAdministratorGroups(await response.json(), type, params);
    case "customer_key_rotation_drafts":
      return incomingCustomerKeyRotationDrafts(await response.json(), type, params);
    case "internal_permissions":
      return incomingPermissions(await response.json(), type, params);
    case "gpx_files":
      if (type === "UPDATE" && params.body) {
        return incomingGPXFiles([], type, params);
      } else if (type === "GET_ONE" && !params.filter.here_download) {
        return incomingGPXFiles(await response.blob(), type, params);
      }
      return incomingGPXFiles(await response.json(), type, params);
    case "document_type_format_associations":
      return incomingDocFormatAssociations(await response.json(), type, params);
    case "issue_report":
      return incomingIssueReports(await response.json(), type, params);
    case "issue_report_download":
      return incomingIssueReportDownload(await response.json(), type, params);
    case "trip_routes":
      return incomingTripRoutes(await response.json(), type, params);
    case "trip_plans":
      const json = await response.json();
      if (typeof json === "string") {
        return incomingTripPlans(JSON.parse(json), type, params);
      }
      return incomingTripPlans(json, type, params);
    case "routes":
      return incomingRoutes(await response.json(), type, params);
    case "sendgrid":
      return incomingSendgrid(await response.json(), type, params);
    case "backend_user_role_drafts":
      return incomingBackendUserRoleDrafts(await response.json(), type, params);
    case "trigger_builds":
      if (type === "UPDATE" && params.body) {
        return incomingTriggerBuilds([], type, params);
      }
      return incomingTriggerBuilds(await response.json(), type, params);
    case "archived_builds":
      return incomingArchivedBuilds(await response.json(), type, params);
    case "here_call":
      return incomingHERECalls(await response.json(), type, params);
    case "app_releases":
      return incomingAppReleases(await response.json(), type, params);
    case "bulk_triggers":
      return incomingBulkTriggers(await response.json(), type, params);
    case "patches":
      return incomingPluggablePatching(await response.json(), type, params);
    case "messages":
      return incomingMessages(await response.json(), type, params);
    case "platform_users":
      return incomingPlatformUsers(await response.json(), type, params);
    case "pluggable_patch":
      return incomingPatches(await response.json(), type, params);
    default:
      return baseIncomingRequest(await response.json(), type, params);
  }
};

const sendBackendUserInvite = (email, firstName, lastName, clientKey) => {
  var requestOptions = {
    method: "PUT",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" },
    body: JSON.stringify({ email: email, first_name: firstName, last_name: lastName }),
  };
  var requestUrl = `${API_URL}/internal/clients/invite/${clientKey}`;

  fetch(requestUrl, requestOptions).then((response) => {
    if (response.ok) {
      response.json().then((resp) => {
        alert(`${email} was sucessfully invited to be a backend user for ${clientKey}`);
      });
    } else {
      alert(`${email}'s invite failed. Status: ${response.status}`);
    }
  });
};

const sendRecvDataProviderRequest = async (type, resource, url, options, params) => {
  const resourcesWithoutGetOne = ["menus", "dashboards", "actions", "screens", "forms", "errors"];
  const emptyResponse = { data: { id: params?.id } };
  let responseClone,
    errorClone = null;

  if (
    (type === "GET_ONE" && (resourcesWithoutGetOne.includes(resource) || resource === "per_load_billing_structure")) ||
    (type === "UPDATE" && resource === "customers" && !params?.data)
  )
    return emptyResponse;
  else if (type === "GET_LIST" && !params?.filter && ["customers", "scanning_users", "documents"].includes(resource))
    return convertHTTPResponseToDataProvider(new Response(JSON.stringify([{ key: "empty" }])), type, resource, params);

  try {
    const response = await fetch(url, options);
    if (!response) throw new Error("Fetch Error: No response received or CORS policy.");

    if (!response.ok) {
      const cloneForError = response.clone();
      let responseBody = await cloneForError.text();
      let parsedBody = responseBody;
      try {
        parsedBody = JSON.parse(responseBody);
      } catch {
        // If parsing fails, keep it as text
      }
      const empty = await handleEmpty(resource, type, response.status, parsedBody);
      if (empty !== null) {
        const emptyError = new Error(`${type} failed for ${resource}.`, { status: response.status });
        sendSentryException(emptyError, resource, type, parsedBody);
        return empty;
      }
      throw await rejectPromise(response, resource, parsedBody);
    }

    if (type === "CREATE" && resource === "clients" && response.status === 201) {
      const body = JSON.parse(options.body);
      body.invite_emails?.forEach((invite) =>
        sendBackendUserInvite(invite.email, invite.first_name, invite.last_name, body.key)
      );
    }

    responseClone = response.clone(); // For convertHTTPResponseToDataProvider
    errorClone = response.clone(); // For catch block
    return await convertHTTPResponseToDataProvider(responseClone, type, resource, params);
  } catch (error) {
    if (typeof error === "object" && error !== null) {
      error.url = typeof url === "string" ? url.split("?")[0] : url;
    }

    let responseBody = error.responseBody;
    if (!responseBody && errorClone) {
      responseBody = await errorClone.text();
      try {
        responseBody = JSON.parse(responseBody);
      } catch {
        // If parsing fails, keep it as text
      }
    }
    throwSentryError(error, resource, type, responseBody);
  }
};

/**
 * @param {string} type Request type, e.g GET_LIST
 * @param {string} resource Resource name, e.g. "posts"
 * @param {Object} payload Request parameters. Depends on the request type
 * @returns {Promise} the Promise for response
 */
const dataProvider = (type, resource, params, authorization) => {
  localStorage.removeItem("searchCriteria");
  let { url, options = {} } = convertDataProviderRequestToHTTP(type, resource, params);
  options.headers = new Headers(options.headers || {});
  if (["trip_plans", "trigger_builds", "messages"].includes(resource)) {
    options.headers.set("Eleos-Admin-Client-Key", params[resource === "trip_plans" ? "filter" : "meta"].clientKey);
  }
  const authRules = {
    issue_report_download: () => false,
    here_call: () => false,
    gpx_files: () => type !== "GET_ONE" && !(type === "UPDATE" && params.body),
    app_assets: () => !(type === "UPDATE" && !params.getPresigned),
    alt_dashboard_logos: () => type !== "UPDATE",
    default: () => true,
  };
  const shouldSetAuthHeader = authRules[resource]?.() ?? authRules.default();
  if (shouldSetAuthHeader) options.headers.set("Authorization", authorization);
  if (["messages"].includes(resource)) {
    options.headers.set("Content-Type", "application/x-www-form-url-encoded;charset=UTF-8");
    return sendRecvDataProviderRequest(type, resource, url, options, params);
  }
  if (
    !(resource === "alt_dashboard_logos" && type === "UPDATE") &&
    !(resource === "app_assets" && (type === "UPDATE") & !params.getPresigned)
  ) {
    options.headers.set("Content-Type", "application/json");
  }
  return sendRecvDataProviderRequest(type, resource, url, options, params);
};

export default dataProvider;
export { convertDataProviderRequestToHTTP };
